import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { useTranslation } from "react-i18next";

const toneClass = {
  primary: "border-transparent bg-[var(--ds-card-sun)] text-[var(--ds-on-card)] hover:brightness-95",
  // Selected controls use the same card-sun fill as the catalog's active pill.
  pressed:
    "border-transparent bg-[var(--ds-card-sun)] text-[var(--ds-on-card)]",
  // Secondary actions follow Button's 1.5px ink outline and stay visually subordinate.
  secondary:
    "border-[1.5px] border-[var(--ds-ink)] bg-transparent text-[var(--ds-ink)] hover:bg-[var(--ds-surface-raised)]",
  danger: "border-transparent bg-[var(--ds-card-flame)] text-[var(--ds-on-card)] hover:brightness-95",
  // Destructive, but a peer among other actions rather than the one thing on a confirm
  // dialog. Two saturated fills in one bar fight each other and neither reads as primary.
  dangerSoft:
    "border-transparent bg-[var(--app-danger-soft)] text-[var(--app-danger-text)] hover:bg-[var(--app-danger-soft-hover)]",
  ghost: "border-transparent bg-transparent text-[var(--app-text-secondary)] hover:bg-[var(--app-surface-hover)] hover:text-[var(--app-text)]",
} as const;

// Radius and inline padding live apart from the size so `shape` can replace them outright:
// stacking `rounded-full` on top of `rounded-xl` leaves the winner to Tailwind's own rule
// order, which is not something a call site should have to reason about.
const buttonSizeClass = {
  sm: "min-h-11 py-2 text-sm md:min-h-11",
  md: "min-h-[52px] py-3 text-base md:min-h-11 md:text-sm",
  lg: "min-h-14 py-3 text-base md:min-h-12 md:text-sm",
} as const;

const buttonRadiusClass = {
  sm: "rounded-full",
  md: "rounded-full",
  lg: "rounded-full",
} as const;

// A pill runs wider than a rounded rectangle at the same size. Its round ends eat into the
// usable width, so matching padding leaves the label hugging the curve and the button
// reading as a stubby circle rather than a chip.
const buttonPaddingClass = {
  default: { sm: "px-3", md: "px-4", lg: "px-5" },
  pill: { sm: "px-5", md: "px-6", lg: "px-7" },
} as const;

type AppButtonTone = keyof typeof toneClass;
type AppButtonSize = keyof typeof buttonSizeClass;

type AppButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: AppButtonTone;
  size?: AppButtonSize;
  /** `pill` is the filter/chip silhouette — see DESIGN.md, "Pills, filters, FABs". */
  shape?: "default" | "pill";
  fullWidth?: boolean;
  isLoading?: boolean;
  leadingIcon?: ReactNode;
};

export const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
  ({ tone = "secondary", size = "md", shape = "default", fullWidth = false, isLoading = false, leadingIcon, className = "", children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      className={[
        "app-focus-ring inline-flex items-center justify-center gap-2 border font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40",
        buttonSizeClass[size],
        buttonPaddingClass[shape][size],
        shape === "pill" ? "rounded-full" : buttonRadiusClass[size],
        toneClass[tone],
        fullWidth ? "w-full" : "",
        className,
      ].join(" ")}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? <LoadingSpinner className="h-4 w-4" /> : leadingIcon}
      {children}
    </button>
  ),
);

AppButton.displayName = "AppButton";

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: AppButtonTone;
  size?: "sm" | "md";
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ tone = "secondary", size = "md", className = "", children, ...props }, ref) => (
    <button
      ref={ref}
      className={[
        "app-focus-ring inline-flex shrink-0 items-center justify-center rounded-full border font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40",
        size === "sm" ? "h-11 w-11" : "h-12 w-12 md:h-11 md:w-11",
        toneClass[tone],
        className,
      ].join(" ")}
      {...props}
    >
      {children}
    </button>
  ),
);

IconButton.displayName = "IconButton";

export const LoadingSpinner = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg className={["animate-spin", className].join(" ")} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

type FormFieldProps = {
  label: ReactNode;
  htmlFor?: string;
  required?: boolean;
  optionalLabel?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  errorId?: string;
  children: ReactNode;
  className?: string;
};

export const FormField = ({
  label,
  htmlFor,
  required = false,
  optionalLabel,
  helper,
  error,
  errorId,
  children,
  className = "",
}: FormFieldProps) => {
  const { t } = useTranslation("common");

  return (
    <div className={`app-form-field ${className}`}>
      <label htmlFor={htmlFor} className="mb-2 block text-sm font-semibold text-[var(--app-text)]">
        {label}
        {required ? (
          <span className="app-form-required app-field-required">{t("common.labels.required")}</span>
        ) : null}
        {!required && optionalLabel ? (
          <span className="app-form-required ml-1 text-xs font-normal text-[var(--app-muted-text)]">({optionalLabel})</span>
        ) : null}
      </label>
      {children}
      {error ? <p id={errorId} role={errorId ? "alert" : undefined} className="app-form-error mt-2 text-xs text-[var(--app-danger)]">{error}</p> : null}
      {!error && helper ? <p className="app-form-helper mt-2 text-xs text-[var(--app-muted-text)]">{helper}</p> : null}
    </div>
  );
};

// The shared solid focus outline comes from theme.css; a Tailwind ring would stack another.
export const fieldControlClass =
  "app-form-control min-h-[52px] w-full rounded-[var(--ds-radius-md)] border border-[var(--ds-ink-muted)] bg-[var(--ds-surface-raised)] px-4 py-3 text-[var(--ds-ink)] outline-none transition-colors duration-150 placeholder:text-[var(--ds-ink-muted)] hover:border-[var(--ds-ink)] focus-visible:border-[var(--ds-ink)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[var(--ds-ink-muted)] md:min-h-12";

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  inputClassName?: string;
  leftAddon?: ReactNode;
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ className = "", inputClassName = "", leftAddon, ...props }, ref) => {
    if (leftAddon) {
      return (
        <div className={["relative", className].join(" ")}>
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-medium text-[var(--app-muted-text)]">
            {leftAddon}
          </span>
          <input ref={ref} className={[fieldControlClass, "pl-14", inputClassName].join(" ")} {...props} />
        </div>
      );
    }

    return <input ref={ref} className={[fieldControlClass, className, inputClassName].join(" ")} {...props} />;
  },
);

TextInput.displayName = "TextInput";

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className = "", ...props }, ref) => (
    <textarea ref={ref} className={[fieldControlClass, "resize-y", className].join(" ")} {...props} />
  ),
);

TextArea.displayName = "TextArea";

// The shell only exists to carry the chevron: a native arrow is painted by the platform
// and ignores the control's radius, so `appearance: none` removes it and theme.css draws
// a masked one that follows the text color in both themes.
export const SelectInput = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className = "", ...props }, ref) => (
    <div className="app-select-shell relative">
      <select ref={ref} className={[fieldControlClass, "appearance-none pl-4 pr-9", className].join(" ")} {...props} />
    </div>
  ),
);

SelectInput.displayName = "SelectInput";

type ModalActionsProps = {
  cancelLabel: ReactNode;
  submitLabel: ReactNode;
  submittingLabel?: ReactNode;
  onCancel: () => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  submitTitle?: string;
};

export const ModalActions = ({
  cancelLabel,
  submitLabel,
  submittingLabel,
  onCancel,
  onSubmit,
  isSubmitting = false,
  isSubmitDisabled = false,
  submitTitle,
}: ModalActionsProps) => (
  // Single row on mobile (primary first, cancel compact) so a bounded sheet does not
  // spend ~120px of height on two stacked full-width buttons.
  <div className="flex w-full flex-row-reverse items-center gap-2 sm:justify-start sm:gap-3">
    <AppButton
      type={onSubmit ? "button" : "submit"}
      tone="primary"
      onClick={onSubmit}
      disabled={isSubmitDisabled || isSubmitting}
      isLoading={isSubmitting}
      title={submitTitle}
      className="min-w-0 flex-1 px-3 sm:w-auto sm:flex-none sm:px-4"
    >
      <span className="truncate">{isSubmitting && submittingLabel ? submittingLabel : submitLabel}</span>
    </AppButton>
    <AppButton
      type="button"
      tone="secondary"
      onClick={onCancel}
      disabled={isSubmitting}
      className="shrink-0 px-4 sm:w-auto"
    >
      {cancelLabel}
    </AppButton>
  </div>
);

type MessageBannerProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  tone?: "default" | "error" | "success";
};

export const MessageBanner = ({ children, tone = "default", className = "", ...props }: MessageBannerProps) => {
  const toneClasses = {
    default: "bg-[var(--app-surface-soft)] text-[var(--app-text-secondary)] ring-[color:color-mix(in_srgb,var(--app-border)_70%,transparent)]",
    error: "bg-[var(--app-danger-soft)] text-[var(--app-danger)] ring-[color:color-mix(in_srgb,var(--app-danger)_28%,transparent)]",
    success: "bg-[var(--app-success-chip-bg)] text-[var(--app-success)] ring-[color:color-mix(in_srgb,var(--app-success)_28%,transparent)]",
  } as const;

  return (
    <div
      className={["rounded-[var(--ds-radius-md)] px-5 py-4 text-sm ring-1", toneClasses[tone], className].join(" ")}
      {...props}
    >
      {children}
    </div>
  );
};

type StatTileProps = {
  label: ReactNode;
  value: ReactNode;
  tone?: "default" | "primary" | "subtle" | "success" | "danger";
  className?: string;
};

export const StatTile = ({ label, value, tone = "default", className = "" }: StatTileProps) => {
  const toneClasses = {
    default: "border-[var(--app-border)] bg-[var(--app-surface)]",
    primary: "border-[var(--app-primary-divider)] bg-[var(--app-primary-chip-bg)]",
    subtle: "border-[var(--app-border)] bg-[var(--app-panel-muted-bg)]",
    success: "border-[var(--app-success-border)] bg-[var(--app-success-chip-bg)]",
    danger: "border-[var(--app-danger-border)] bg-[var(--app-danger-panel-bg)]",
  } as const;
  const valueToneClasses = {
    default: "text-[var(--app-text)]",
    primary: "text-[var(--app-primary)]",
    subtle: "text-[var(--app-text)]",
    success: "text-[var(--app-success-text)]",
    danger: "text-[var(--app-danger-text)]",
  } as const;

  return (
  <div className={["min-w-0 rounded-[var(--ds-radius-lg)] border px-4 py-3 sm:px-5 sm:py-4", toneClasses[tone], className].join(" ")}>
      <dt className="text-[10px] font-semibold uppercase leading-tight tracking-[0.16em] text-[var(--app-muted-text)]">{label}</dt>
      <dd className={["mt-2 break-words text-2xl font-semibold leading-tight tracking-[-0.04em] tabular-nums sm:text-3xl", valueToneClasses[tone]].join(" ")}>{value}</dd>
    </div>
  );
};

type EmptyStateProps = {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
};

export const EmptyState = ({ title, body, action, className = "" }: EmptyStateProps) => (
  <div className={["rounded-[var(--ds-radius-lg)] border border-dashed border-[var(--app-border)] bg-[color:color-mix(in_srgb,var(--app-surface)_68%,transparent)] px-5 py-9 text-center", className].join(" ")}>
    <div className="mx-auto mb-4 h-10 w-10 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-soft)]" aria-hidden="true" />
    <h3 className="text-base font-semibold tracking-[-0.02em] text-[var(--app-text)]">{title}</h3>
    {body ? <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[var(--app-text-secondary)]">{body}</p> : null}
    {action ? <div className="mt-5">{action}</div> : null}
  </div>
);

export const SkeletonBlock = ({ className = "" }: { className?: string }) => (
  <div className={["app-skeleton rounded-[var(--ds-radius-sm)]", className].join(" ")} aria-hidden="true" />
);
